import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { Mail, MessageSquare, Zap, AlertTriangle, Lock, Settings, ExternalLink, Plus, X } from 'lucide-react';
export function NotificationsPage() {
  const navigate = useNavigate();
  // Tab state
  const [activeTab, setActiveTab] = useState<'email' | 'sms'>('email');
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Email tab state
  const [emailProvider, setEmailProvider] = useState('shopify');
  const [klaviyoConnected, setKlaviyoConnected] = useState(false);
  // Customer notifications
  const [subscriptionCreated, setSubscriptionCreated] = useState(true);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(true);
  const [subscriptionPaused, setSubscriptionPaused] = useState(true);
  const [subscriptionResumed, setSubscriptionResumed] = useState(true);
  const [subscriptionCancelled, setSubscriptionCancelled] = useState(true);
  const [upcomingOrderReminder, setUpcomingOrderReminder] = useState(true);
  const [orderProcessed, setOrderProcessed] = useState(false);
  const [orderShipped, setOrderShipped] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(true);
  const [paymentMethodExpiring, setPaymentMethodExpiring] = useState(true);
  const [paymentRecovered, setPaymentRecovered] = useState(true);
  // Upcoming order reminder settings
  const [reminderDays, setReminderDays] = useState(3);
  const [includeOrderSummary, setIncludeOrderSummary] = useState(true);
  const [includeOrderTotal, setIncludeOrderTotal] = useState(true);
  const [includeEditButton, setIncludeEditButton] = useState(true);
  const [includeSkipButton, setIncludeSkipButton] = useState(true);
  const [includePauseButton, setIncludePauseButton] = useState(false);
  const [includeCancelButton, setIncludeCancelButton] = useState(false);
  // Admin notifications
  const [adminNewSubscription, setAdminNewSubscription] = useState(true);
  const [adminCancellation, setAdminCancellation] = useState(true);
  const [adminPaymentFailure, setAdminPaymentFailure] = useState(true);
  const [adminDailySummary, setAdminDailySummary] = useState(false);
  const [adminWeeklyReport, setAdminWeeklyReport] = useState(false);
  // SMS tab state
  const [smsProvider, setSmsProvider] = useState<'none' | 'klaviyo' | 'postscript'>('none');
  const [smsUpcomingOrder, setSmsUpcomingOrder] = useState(true);
  const [smsUpcomingOrderDays, setSmsUpcomingOrderDays] = useState(3);
  const [smsPaymentFailed, setSmsPaymentFailed] = useState(true);
  const [smsPaymentRecovered, setSmsPaymentRecovered] = useState(false);
  const [smsOrderShipped, setSmsOrderShipped] = useState(false);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(true);
  const [quietHoursStart, setQuietHoursStart] = useState('9:00 PM');
  const [quietHoursEnd, setQuietHoursEnd] = useState('8:00 AM');
  // Integrations tab state
  const [klaviyoIntegrationConnected, setKlaviyoIntegrationConnected] = useState(true);
  const [webhooksExpanded, setWebhooksExpanded] = useState(false);
  // Klaviyo sync settings
  const [syncSubscriptionStatus, setSyncSubscriptionStatus] = useState(true);
  const [syncStartDate, setSyncStartDate] = useState(true);
  const [syncNextOrderDate, setSyncNextOrderDate] = useState(true);
  const [syncTotalOrders, setSyncTotalOrders] = useState(true);
  const [syncLifetimeValue, setSyncLifetimeValue] = useState(true);
  const [syncCurrentProducts, setSyncCurrentProducts] = useState(true);
  const [syncFrequency, setSyncFrequency] = useState(false);
  const [syncCustomerTags, setSyncCustomerTags] = useState(false);
  const [syncEventCreated, setSyncEventCreated] = useState(true);
  const [syncEventCancelled, setSyncEventCancelled] = useState(true);
  const [syncEventPaused, setSyncEventPaused] = useState(true);
  const [syncEventResumed, setSyncEventResumed] = useState(true);
  const [syncEventOrderCreated, setSyncEventOrderCreated] = useState(true);
  const [syncEventOrderShipped, setSyncEventOrderShipped] = useState(false);
  const [syncEventPaymentFailed, setSyncEventPaymentFailed] = useState(true);
  const [syncEventPaymentRecovered, setSyncEventPaymentRecovered] = useState(true);
  const [syncFrequencyType, setSyncFrequencyType] = useState('realtime');
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const timeOptions = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM'];
  const customerNotifications = [{
    id: 'created',
    label: 'Subscription Created',
    description: 'Sent when a new subscription is created',
    enabled: subscriptionCreated,
    setEnabled: setSubscriptionCreated
  }, {
    id: 'updated',
    label: 'Subscription Updated',
    description: 'Sent when subscription details change',
    enabled: subscriptionUpdated,
    setEnabled: setSubscriptionUpdated
  }, {
    id: 'paused',
    label: 'Subscription Paused',
    description: 'Sent when a subscription is paused',
    enabled: subscriptionPaused,
    setEnabled: setSubscriptionPaused
  }, {
    id: 'resumed',
    label: 'Subscription Resumed',
    description: 'Sent when a paused subscription resumes',
    enabled: subscriptionResumed,
    setEnabled: setSubscriptionResumed
  }, {
    id: 'cancelled',
    label: 'Subscription Cancelled',
    description: 'Sent when a subscription is cancelled',
    enabled: subscriptionCancelled,
    setEnabled: setSubscriptionCancelled
  }, {
    id: 'reminder',
    label: 'Upcoming Order Reminder',
    description: 'Sent before the next order processes',
    enabled: upcomingOrderReminder,
    setEnabled: setUpcomingOrderReminder
  }, {
    id: 'order-processed',
    label: 'Order Processed',
    description: 'Sent when a subscription order is created',
    enabled: orderProcessed,
    setEnabled: setOrderProcessed
  }, {
    id: 'order-shipped',
    label: 'Order Shipped',
    description: 'Sent when order tracking is available',
    enabled: orderShipped,
    setEnabled: setOrderShipped
  }, {
    id: 'payment-failed',
    label: 'Payment Failed',
    description: 'Sent when a payment attempt fails',
    enabled: paymentFailed,
    setEnabled: setPaymentFailed
  }, {
    id: 'payment-expiring',
    label: 'Payment Method Expiring',
    description: 'Sent when card is about to expire',
    enabled: paymentMethodExpiring,
    setEnabled: setPaymentMethodExpiring
  }, {
    id: 'payment-recovered',
    label: 'Payment Recovered',
    description: 'Sent when a failed payment is recovered',
    enabled: paymentRecovered,
    setEnabled: setPaymentRecovered
  }];
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-11374" data-mp="10fvn3ige437e">
      {/* NavBar */}
      <div className="mb-6" data-id="element-11375" data-mp="1eev17w6a0zpb">
        <NavBar tabs={[]} title="Notifications" subtitle="Configure email and SMS notifications for subscription events" data-id="element-11376" data-mp="1ef4q1gfl50tf" />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm mb-6" data-id="element-11377" data-mp="1obzojzbuz17p">
        <div className="flex gap-8 px-6" data-id="element-11378" data-mp="1jdfzvr5vrbbn">
          <button onClick={() => setActiveTab('email')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'email' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-11379" data-mp="1jcemhg1e6cqr">
            Email
          </button>
          <button onClick={() => setActiveTab('sms')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'sms' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-11380" data-mp="2729388ou0lga">
            SMS
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6" data-id="element-11381" data-mp="3edwux8dmwxc1">
        {/* EMAIL TAB */}
        {activeTab === 'email' && <>
            {/* Section 1: Email Provider */}
            <Card data-id="element-11382" data-mp="3nfw9lu16fpbt">
              <div className="mb-6" data-id="element-11383" data-mp="3ngec38efmzs6">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11384" data-mp="3nfd1f51bwfaz">
                  Email Provider
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11385" data-mp="2urgz9kszpv6t">
                  Choose how subscription emails are sent
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11386" data-mp="2zp0jr2ga7477">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${emailProvider === 'shopify' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-11387" data-mp="34ibw1j3v4d6i">
                  <input type="radio" name="emailProvider" value="shopify" checked={emailProvider === 'shopify'} onChange={e => {
                setEmailProvider(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-11388" data-mp="2bzeour6xzb50" />
                  <div className="flex-1" data-id="element-11389" data-mp="1uhhb31zdivuq">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11390" data-mp="1qi8g5tslkrxl">
                      <Mail className="w-5 h-5 text-[#0EA5E9]" data-id="element-11391" data-mp="1qk1oddiuzk7p" />
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11392" data-mp="1ljdyiv6d99f2">
                        Shopify Email
                      </span>
                      <Badge variant="default" data-id="element-11393" data-mp="1bsstoq03n8np">Default</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-11394" data-mp="1bqgak9ilus92">
                      Use Shopify's built-in email system
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${emailProvider === 'klaviyo' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-11395" data-mp="12u79yriw5nlc">
                  <input type="radio" name="emailProvider" value="klaviyo" checked={emailProvider === 'klaviyo'} onChange={e => {
                setEmailProvider(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-11396" data-mp="3fz7xcfjy1m24" />
                  <div className="flex-1" data-id="element-11397" data-mp="31yne5ia49o83">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11398" data-mp="31zp0iiwl1x54">
                      <Mail className="w-5 h-5 text-[#0EA5E9]" data-id="element-11399" data-mp="2x0vqj0gi8o9p" />
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11400" data-mp="3bxcxzglkpiqz">
                        Klaviyo
                      </span>
                      {klaviyoConnected ? <Badge variant="success" data-id="element-11401" data-mp="36wozk9v0lzmq">Connected</Badge> : <button className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-11402" data-mp="r6vopw30e59l">
                          Connect →
                        </button>}
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-11403" data-mp="tr0myt515w5o">
                      Send emails through your Klaviyo account
                    </p>
                  </div>
                </label>
              </div>

              {emailProvider === 'klaviyo' && !klaviyoConnected && <div className="mt-4 p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md" data-id="element-11404" data-mp="kt86hlnkoigp">
                  <p className="text-[14px] text-[#0369A1] mb-3" data-id="element-11405" data-mp="ouabkj467oql">
                    You'll be redirected to authorize WinePik
                  </p>
                  <Button variant="primary" size="sm" onClick={() => navigate('/integrations')} data-id="element-11406" data-mp="1hg347zvr5mzn">
                    Connect Klaviyo
                  </Button>
                </div>}
            </Card>

            {/* Section 2: Customer Email Notifications */}
            <Card data-id="element-11407" data-mp="1vgxfcqa6q220">
              <div className="mb-6" data-id="element-11408" data-mp="1vgdy7bsczhp1">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11409" data-mp="1vfclz3zlwp5q">
                  Customer Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11410" data-mp="209gqlglegl3n">
                  Emails sent to your subscribers
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-11411" data-mp="263c7bb0zxgrf">
                {customerNotifications.map((notification, index) => <div key={notification.id} className={`flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors ${index !== customerNotifications.length - 1 ? 'border-b border-[#E2E8F0]' : ''}`} style={{
              minHeight: '64px'
            }} data-id="element-11412" data-mp="214r7vdtkj141" data-mpi={index}>
                    <div className="flex-1" data-id="element-11413" data-mp="1mch0khe59v07">
                      <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-11414" data-mp="1ic6t10apewj8">
                        {notification.label}
                      </h3>
                      <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-11415" data-mp="1dewxr40mvqnh">
                        {notification.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-11416" data-mp="13g7bz05m9m1y">
                      <div className="flex items-center gap-2" data-id="element-11417" data-mp="tb92zj0ru6ug">
                        <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-11418" data-mp="tazczvfq7zor">
                          Preview
                        </button>
                        <span className="text-[#CBD5E1]" data-id="element-11419" data-mp="139ycanelqiz3">·</span>
                        <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-11420" data-mp="1891hc7ul0p72">
                          Edit
                        </button>
                      </div>
                      <button onClick={() => {
                  notification.setEnabled(!notification.enabled);
                  saveSetting();
                }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-11421" data-mp="fkvxrof9l68t">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11422" data-mp="3swe43evtck8z" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Section 3: Upcoming Order Reminder Settings */}
            <Card data-id="element-11423" data-mp="23gtpnq08zobj">
              <div className="mb-6" data-id="element-11424" data-mp="23gain4c4ykca">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11425" data-mp="2h8qe9k2pjg36">
                  Upcoming Order Reminder
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11426" data-mp="2d6myij909pc3">
                  Configure when to send the upcoming order reminder
                </p>
              </div>

              <div className="space-y-6" data-id="element-11427" data-mp="2m7l7zh27qp22">
                <div data-id="element-11428" data-mp="2m8w91949xiph">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-11429" data-mp="2m6a3tg4uvfaf">
                    Send reminder
                  </label>
                  <div className="flex items-center gap-2" data-id="element-11430" data-mp="rv3q6p058h4t">
                    <input type="number" value={reminderDays} onChange={e => {
                  setReminderDays(parseInt(e.target.value) || 0);
                  saveSetting();
                }} min="1" max="30" className="w-20 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-11431" data-mp="33hf9qfzsuw1l" />
                    <span className="text-[14px] text-[#64748B]" data-id="element-11432" data-mp="2yoce82bgpcir">
                      days before order processes
                    </span>
                  </div>
                  <p className="text-[12px] text-[#64748B] mt-1" data-id="element-11433" data-mp="2prlmg23y0jtz">
                    Customers can make changes before this cutoff
                  </p>
                </div>

                <div data-id="element-11434" data-mp="2fr1iussyb38l">
                  <label className="block text-[12px] font-medium text-[#475569] mb-3" data-id="element-11435" data-mp="2fscvli4djlbu">
                    Include in reminder
                  </label>
                  <div className="space-y-3" data-id="element-11436" data-mp="e07v3i8t7fpi">
                    {[{
                  label: 'Order summary with products',
                  enabled: includeOrderSummary,
                  setEnabled: setIncludeOrderSummary
                }, {
                  label: 'Order total',
                  enabled: includeOrderTotal,
                  setEnabled: setIncludeOrderTotal
                }, {
                  label: '"Edit order" button',
                  enabled: includeEditButton,
                  setEnabled: setIncludeEditButton
                }, {
                  label: '"Skip this order" button',
                  enabled: includeSkipButton,
                  setEnabled: setIncludeSkipButton
                }, {
                  label: '"Pause subscription" button',
                  enabled: includePauseButton,
                  setEnabled: setIncludePauseButton
                }, {
                  label: '"Cancel subscription" button',
                  enabled: includeCancelButton,
                  setEnabled: setIncludeCancelButton
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-11437" data-mp="2rwl470pkkjwy" data-mpi={index}>
                        <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-11438" data-mp="2wv4qj282rxpu" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-11439" data-mp="35tq5yoi92wme">
                          {item.label}
                        </span>
                      </label>)}
                  </div>
                </div>
              </div>
            </Card>

            {/* Section 4: Admin Email Notifications */}
            <Card data-id="element-11440" data-mp="3omkmhmviuwsq">
              <div className="mb-6" data-id="element-11441" data-mp="3on3uaargk4t1">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11442" data-mp="3om2ha0qvt88o">
                  Admin Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11443" data-mp="3tju9kvvfuwde">
                  Emails sent to store owners and staff
                </p>
              </div>

              <div className="space-y-6" data-id="element-11444" data-mp="39gb7o1du9ys0">
                {[{
              title: 'New subscription notification',
              description: 'Receive email when a new subscription is created',
              enabled: adminNewSubscription,
              setEnabled: setAdminNewSubscription
            }, {
              title: 'Cancellation notification',
              description: 'Receive email when a subscription is cancelled',
              enabled: adminCancellation,
              setEnabled: setAdminCancellation
            }, {
              title: 'Payment failure notification',
              description: 'Receive email when a payment fails',
              enabled: adminPaymentFailure,
              setEnabled: setAdminPaymentFailure
            }, {
              title: 'Daily summary',
              description: 'Receive a daily email summarizing subscription activity',
              enabled: adminDailySummary,
              setEnabled: setAdminDailySummary
            }, {
              title: 'Weekly report',
              description: 'Receive a weekly report with subscription metrics',
              enabled: adminWeeklyReport,
              setEnabled: setAdminWeeklyReport
            }].map((notification, index) => <div key={index} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-11445" data-mp="1ixl66lni5712" data-mpi={index}>
                    <div className="flex-1 pr-6" data-id="element-11446" data-mp="2bod1lgwqtrnc">
                      <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-11447" data-mp="2bov3ou637036">
                        {notification.title}
                      </h3>
                      <p className="text-[14px] text-[#64748B]" data-id="element-11448" data-mp="21qffayyg24xd">
                        {notification.description}
                      </p>
                    </div>
                    <button onClick={() => {
                notification.setEnabled(!notification.enabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-11449" data-mp="26qtmyfw4vcuv">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11450" data-mp="3i8rt2rm2h7n4" />
                    </button>
                  </div>)}

                <div className="pt-6 border-t border-[#E2E8F0]" data-id="element-11451" data-mp="38ip2ey2f45ry">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-11452" data-mp="3db05g9sgg7oq">
                    Send admin notifications to
                  </label>
                  <div className="flex items-center gap-2 mb-3" data-id="element-11453" data-mp="33kwz77wbox8g">
                    <input type="email" placeholder="email@example.com" className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-11454" data-mp="33lglt1wx49st" />
                    <Button variant="secondary" size="sm" data-id="element-11455" data-mp="2ay6z7rppxen4">
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-11456" data-mp="260nm4v4rb7wr">
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#E0F2FE] text-[#0369A1] rounded-full text-[13px]" data-id="element-11457" data-mp="2kwd48i6tn0w7">
                      orders@acmewine.com
                      <button className="hover:text-[#EF4444] transition-colors" data-id="element-11458" data-mp="2fzwfpmvpmkil">
                        ×
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </>}

        {/* SMS TAB */}
        {activeTab === 'sms' && <>
            {/* Section 1: SMS Provider */}
            <Card data-id="element-11459" data-mp="2ypcjqk6lsv3r">
              <div className="mb-6" data-id="element-11460" data-mp="2yot6hgrweuw2">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11461" data-mp="3cnj3yximqwky">
                  SMS Provider
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11462" data-mp="38n8omu8y6ttf">
                  Connect an SMS service to send text notifications
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-11463" data-mp="3ko8ml2navgeq">
                <div className="p-4 border border-[#E2E8F0] rounded-lg hover:shadow-md transition-shadow" data-id="element-11464" data-mp="3km5odvbzugyx">
                  <div className="flex items-start justify-between mb-3" data-id="element-11465" data-mp="3uebhj8dzf4fp">
                    <div className="flex items-center gap-3" data-id="element-11466" data-mp="3ugeg4gt7a5w1">
                      <MessageSquare className="w-8 h-8 text-[#0EA5E9]" data-id="element-11467" data-mp="29hcv7klln8c" />
                      <div data-id="element-11468" data-mp="79c5pgnxomw1">
                        <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11469" data-mp="78keei1ddbbk">
                          Klaviyo SMS
                        </h3>
                        <Badge variant="default" className="mt-1" data-id="element-11470" data-mp="c6uacuux4fy8">
                          Not Connected
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-11471" data-mp="2iyv4q15guwme">
                    Send SMS through Klaviyo
                  </p>
                  <Button variant="primary" size="sm" onClick={() => {
                setSmsProvider('klaviyo');
                saveSetting('Klaviyo SMS connected');
              }} data-id="element-11472" data-mp="10520iytydkyt">
                    Connect
                  </Button>
                </div>

                <div className="p-4 border border-[#E2E8F0] rounded-lg hover:shadow-md transition-shadow" data-id="element-11473" data-mp="mcvmpje9vq2j">
                  <div className="flex items-start justify-between mb-3" data-id="element-11474" data-mp="1j3edkvnzjezo">
                    <div className="flex items-center gap-3" data-id="element-11475" data-mp="1j0jt544z1u69">
                      <MessageSquare className="w-8 h-8 text-[#0EA5E9]" data-id="element-11476" data-mp="1f1stoqd1a0fz" />
                      <div data-id="element-11477" data-mp="1a1euzyxbo2um">
                        <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11478" data-mp="1a0mzsp7n074v">
                          Postscript
                        </h3>
                        <Badge variant="default" className="mt-1" data-id="element-11479" data-mp="3us5n8n2ikn87">
                          Not Connected
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-11480" data-mp="1hke4c3ucuxtx">
                    Send SMS through Postscript
                  </p>
                  <Button variant="primary" size="sm" onClick={() => {
                setSmsProvider('postscript');
                saveSetting('Postscript connected');
              }} data-id="element-11481" data-mp="1mcxgcwde9kwm">
                    Connect
                  </Button>
                </div>
              </div>

              {smsProvider === 'none' && <div className="p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-3" data-id="element-11482" data-mp="219eutwgvrfne">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-11483" data-mp="257w0scokek38" />
                  <p className="text-[14px] text-[#92400E]" data-id="element-11484" data-mp="1rcjvjbcs9b82">
                    Connect an SMS provider to enable text notifications. SMS
                    can significantly improve payment recovery rates.
                  </p>
                </div>}
            </Card>

            {/* Section 2: SMS Notifications */}
            <Card data-id="element-11485" data-mp="dauiiunwqnl1">
              <div className="mb-6" data-id="element-11486" data-mp="dacnu2gw8hf8">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11487" data-mp="d9azwxes40g3">
                  SMS Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11488" data-mp="8e41a40d9ly1">
                  Text messages sent to subscribers who opt in
                </p>
              </div>

              <div className={`space-y-6 ${smsProvider === 'none' ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-11489" data-mp="hcz9tmqvv84i">
                {smsProvider === 'none' && <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg" data-id="element-11490" data-mp="3lqnz19i8nz23">
                    <p className="text-[16px] font-medium text-[#64748B]" data-id="element-11491" data-mp="3d80hzbbafwu">
                      Connect an SMS provider to configure notifications
                    </p>
                  </div>}

                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-11492" data-mp="371hz1jbku4t5">
                  <div className="flex-1 pr-6" data-id="element-11493" data-mp="322ygzu03td62">
                    <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-11494" data-mp="3225dd1wfz3s5">
                      Upcoming order reminder
                    </h3>
                    <p className="text-[14px] text-[#64748B] mb-2" data-id="element-11495" data-mp="1ay3ieib8xleh">
                      Text reminder before order processes
                    </p>
                    <div className="flex items-center gap-2" data-id="element-11496" data-mp="1fq522paqueej">
                      <span className="text-[14px] text-[#64748B]" data-id="element-11497" data-mp="1knepy0ihii09">Send</span>
                      <input type="number" value={smsUpcomingOrderDays} onChange={e => {
                    setSmsUpcomingOrderDays(parseInt(e.target.value) || 0);
                    saveSetting();
                  }} min="1" max="30" className="w-16 h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-11498" data-mp="n0xxb8kp4sku" />
                      <span className="text-[14px] text-[#64748B]" data-id="element-11499" data-mp="28esia6gmydl8">
                        days before
                      </span>
                    </div>
                  </div>
                  <button onClick={() => {
                setSmsUpcomingOrder(!smsUpcomingOrder);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${smsUpcomingOrder ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-11500" data-mp="s0wd5tt99l9">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smsUpcomingOrder ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11501" data-mp="3j4fh43sc7s53" />
                  </button>
                </div>

                {[{
              title: 'Payment failed',
              description: 'Alert when payment fails',
              enabled: smsPaymentFailed,
              setEnabled: setSmsPaymentFailed
            }, {
              title: 'Payment recovered',
              description: 'Confirmation when payment is recovered',
              enabled: smsPaymentRecovered,
              setEnabled: setSmsPaymentRecovered
            }, {
              title: 'Order shipped',
              description: 'Notification with tracking info',
              enabled: smsOrderShipped,
              setEnabled: setSmsOrderShipped
            }].map((notification, index) => <div key={index} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-11502" data-mp="3tpqb6hbhm6ld" data-mpi={index}>
                    <div className="flex-1 pr-6" data-id="element-11503" data-mp="2hlwglxg5lcj">
                      <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-11504" data-mp="3koida0cvv7rt">
                        {notification.title}
                      </h3>
                      <p className="text-[14px] text-[#64748B]" data-id="element-11505" data-mp="3ardqpzf4laah">
                        {notification.description}
                      </p>
                    </div>
                    <button onClick={() => {
                notification.setEnabled(!notification.enabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-11506" data-mp="3fqqo3e3mhzrb">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11507" data-mp="2tjmm04h3zwp6" />
                    </button>
                  </div>)}
              </div>
            </Card>

            {/* Section 3: SMS Compliance */}
            <Card data-id="element-11508" data-mp="3h5t45jk8yst4">
              <div className="mb-6" data-id="element-11509" data-mp="3h9gte6inst21">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11510" data-mp="3c9cgsrkvgih3">
                  Compliance Settings
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11511" data-mp="3r5236qtyyxmc">
                  Ensure SMS messages meet regulatory requirements
                </p>
              </div>

              <div className={`space-y-6 ${smsProvider === 'none' ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-11512" data-mp="1wsu0usfbjegj">
                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-11513" data-mp="1ru0xhstqx3u3">
                  <div className="flex-1 pr-6" data-id="element-11514" data-mp="3mgvke0oem702">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11515" data-mp="3hj3oywojw2r4">
                      <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11516" data-mp="3hjvm4bxsq8ji">
                        Require SMS opt-in
                      </h3>
                      <Lock className="w-4 h-4 text-[#94A3B8]" data-id="element-11517" data-mp="38rblx71uvvwk" />
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-1" data-id="element-11518" data-mp="33rfj3b4waail">
                      Customers must explicitly opt in to receive SMS
                    </p>
                    <p className="text-[12px] text-[#94A3B8]" data-id="element-11519" data-mp="2yup1gjdfz6v2">
                      Required by law in most regions
                    </p>
                  </div>
                  <button disabled className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#0EA5E9] opacity-50 cursor-not-allowed flex-shrink-0" data-id="element-11520" data-mp="2psz9ncfne5wr">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-6" data-id="element-11521" data-mp="o0ksifkxh5he" />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-11522" data-mp="25xo7jeox9p7i">
                  <div className="flex-1 pr-6" data-id="element-11523" data-mp="2avpsy69tm0m5">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11524" data-mp="2fo96524litqf">
                      <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-11525" data-mp="2fq3qofbygjtz">
                        Include opt-out instructions
                      </h3>
                      <Lock className="w-4 h-4 text-[#94A3B8]" data-id="element-11526" data-mp="1n0d91wb46zt4" />
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-1" data-id="element-11527" data-mp="1rxmy3auld9gf">
                      Every SMS includes 'Reply STOP to unsubscribe'
                    </p>
                    <p className="text-[12px] text-[#94A3B8]" data-id="element-11528" data-mp="1w2crmt1s2by6">
                      Required by law
                    </p>
                  </div>
                  <button disabled className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#0EA5E9] opacity-50 cursor-not-allowed flex-shrink-0" data-id="element-11529" data-mp="37e59qxt43rb6">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-6" data-id="element-11530" data-mp="1cc0ht4mqgrkk" />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4" data-id="element-11531" data-mp="189vmm1gmzmvt">
                  <div className="flex-1 pr-6" data-id="element-11532" data-mp="1mag4mveq9eo9">
                    <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-11533" data-mp="1m83omnsj5edu">
                      Quiet hours
                    </h3>
                    <p className="text-[14px] text-[#64748B] mb-3" data-id="element-11534" data-mp="tkr5knlkvopa">
                      Don't send SMS during nighttime hours
                    </p>
                    {quietHoursEnabled && <div className="flex items-center gap-2 flex-wrap" data-id="element-11535" data-mp="13bc0o121mb3g">
                        <span className="text-[14px] text-[#64748B]" data-id="element-11536" data-mp="13dez99h9hcjs">
                          No SMS between
                        </span>
                        <select value={quietHoursStart} onChange={e => {
                    setQuietHoursStart(e.target.value);
                    saveSetting();
                  }} className="h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-11537" data-mp="auhq4c0s60fn">
                          {timeOptions.map(time => <option key={time} value={time} data-id="element-11538" data-mp="28iloescrj2h3">
                              {time}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-11539" data-mp="2ckgndq7813rp">and</span>
                        <select value={quietHoursEnd} onChange={e => {
                    setQuietHoursEnd(e.target.value);
                    saveSetting();
                  }} className="h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-11540" data-mp="30b2v1gdbcm16">
                          {timeOptions.map(time => <option key={time} value={time} data-id="element-11541" data-mp="1ftelzia6tsqk">
                              {time}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-11542" data-mp="1kqocz2d86cgi">
                          in customer's local timezone
                        </span>
                      </div>}
                  </div>
                  <button onClick={() => {
                setQuietHoursEnabled(!quietHoursEnabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${quietHoursEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-11543" data-mp="1qcuo6pmxpymx">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11544" data-mp="2e0ciarto98t2" />
                  </button>
                </div>
              </div>
            </Card>
          </>}

        {/* INTEGRATIONS TAB */}
        {activeTab === 'sms' && <>
            {/* Section 1: Email Marketing Platforms */}
            <Card data-id="element-11545" data-mp="tooo8g0yjuez">
              <div className="mb-6" data-id="element-11546" data-mp="359xmcem28z38">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11547" data-mp="35b0boi3qith1">
                  Email Marketing
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11548" data-mp="3a9a7muxa9y3p">
                  Sync subscription data with your email marketing platform
                </p>
              </div>

              <div className="space-y-4" data-id="element-11549" data-mp="3j25d9pqnxvu0">
                {/* Klaviyo Integration */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow" data-id="element-11550" data-mp="3nzdn931nqtk6">
                  <div className="flex items-start justify-between mb-4" data-id="element-11551" data-mp="3sy8fswpffwhh">
                    <div className="flex items-center gap-3" data-id="element-11552" data-mp="1sp804qjkhd9">
                      <Zap className="w-8 h-8 text-[#0EA5E9]" data-id="element-11553" data-mp="1re1hx5povig" />
                      <div data-id="element-11554" data-mp="5tivix02nu5m">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-11555" data-mp="5spso748hosr">
                          Klaviyo
                        </h3>
                        <Badge variant="success" className="mt-1" data-id="element-11556" data-mp="27lf3xxqy0nw6">
                          Connected
                        </Badge>
                      </div>
                    </div>
                    <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-11557" data-mp="35yjaz9f0k5ua">
                      <Settings className="w-5 h-5 text-[#64748B]" data-id="element-11558" data-mp="3jxj1kmm36ut3" />
                    </button>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-11559" data-mp="3fvxop19jygiw">
                    Sync subscriber data and trigger flows based on subscription
                    events. Perfect for winback campaigns and lifecycle emails.
                  </p>

                  <p className="text-[13px] text-[#94A3B8] mb-4" data-id="element-11560" data-mp="3ourgmebxuiq4">
                    Last synced: 5 minutes ago
                  </p>

                  <div className="flex items-center gap-3" data-id="element-11561" data-mp="2grebhpxmxpr">
                    <Button variant="secondary" size="sm" data-id="element-11562" data-mp="2jdto3j2ehij">
                      Sync Now
                    </Button>
                    <Button variant="ghost" size="sm" data-id="element-11563" data-mp="bhzd00wd20kd">
                      View Sync Settings
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#EF4444]" data-id="element-11564" data-mp="v3hlpiyv9il4">
                      Disconnect
                    </Button>
                  </div>
                </div>

                {/* Mailchimp - Coming Soon */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 opacity-60" data-id="element-11565" data-mp="m2t7oo9h3i88">
                  <div className="flex items-start justify-between mb-4" data-id="element-11566" data-mp="q3d2hl6kq9r7">
                    <div className="flex items-center gap-3" data-id="element-11567" data-mp="cczvpm5f513g">
                      <Mail className="w-8 h-8 text-[#64748B]" data-id="element-11568" data-mp="cbgentbxum0m" />
                      <div data-id="element-11569" data-mp="h5kockcl985e">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-11570" data-mp="h5t2sgod0lip">
                          Mailchimp
                        </h3>
                        <Badge variant="default" className="mt-1 bg-[#EDE9FE] text-[#6F42C1]" data-id="element-11571" data-mp="7f85qxoc3p1w">
                          Coming Soon 🔒
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-11572" data-mp="3ukz1v42j4hwj">
                    Sync subscribers to Mailchimp audiences and trigger
                    automations
                  </p>

                  <Button variant="secondary" size="sm" disabled data-id="element-11573" data-mp="8uc70u0r06fm">
                    Join Waitlist
                  </Button>
                </div>

                {/* Drip - Coming Soon */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 opacity-60" data-id="element-11574" data-mp="3w0v28l7r1f5a">
                  <div className="flex items-start justify-between mb-4" data-id="element-11575" data-mp="3r2tgttmup3qn">
                    <div className="flex items-center gap-3" data-id="element-11576" data-mp="3mb1opeo1qpye">
                      <Mail className="w-8 h-8 text-[#64748B]" data-id="element-11577" data-mp="3m9icbz5igz1w" />
                      <div data-id="element-11578" data-mp="3hcrsr1uhzlbq">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-11579" data-mp="3h9vv7emkaynt">
                          Drip
                        </h3>
                        <Badge variant="default" className="mt-1 bg-[#EDE9FE] text-[#6F42C1]" data-id="element-11580" data-mp="38btrgrbg1rwu">
                          Coming Soon 🔒
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-11581" data-mp="1iwj0ojdgar1x">
                    Connect with Drip for advanced ecommerce automation
                  </p>

                  <Button variant="secondary" size="sm" disabled data-id="element-11582" data-mp="1rw64fbtnk8y5">
                    Join Waitlist
                  </Button>
                </div>
              </div>
            </Card>

            {/* Section 2: Klaviyo Sync Settings */}
            {klaviyoIntegrationConnected && <Card data-id="element-11583" data-mp="1458z4hwd49vv">
                <div className="mb-6" data-id="element-11584" data-mp="146k4gm5ghpp3">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11585" data-mp="143fs31hu7nnc">
                    Klaviyo Sync Settings
                  </h2>
                  <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11586" data-mp="1dvvau10ykg9a">
                    Configure what data syncs to Klaviyo
                  </p>
                </div>

                <div className="space-y-6" data-id="element-11587" data-mp="ge3lm9dw9toq">
                  <div data-id="element-11588" data-mp="le95h3dxabw9">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-11589" data-mp="ldg6iol7gqoo">
                      Subscriber Properties
                    </h3>
                    <div className="space-y-2" data-id="element-11590" data-mp="32lhsrcisoq50">
                      {[{
                  label: 'Subscription status (active, paused, cancelled)',
                  enabled: syncSubscriptionStatus,
                  setEnabled: setSyncSubscriptionStatus
                }, {
                  label: 'Subscription start date',
                  enabled: syncStartDate,
                  setEnabled: setSyncStartDate
                }, {
                  label: 'Next order date',
                  enabled: syncNextOrderDate,
                  setEnabled: setSyncNextOrderDate
                }, {
                  label: 'Total subscription orders',
                  enabled: syncTotalOrders,
                  setEnabled: setSyncTotalOrders
                }, {
                  label: 'Lifetime subscription value',
                  enabled: syncLifetimeValue,
                  setEnabled: setSyncLifetimeValue
                }, {
                  label: 'Current subscription products',
                  enabled: syncCurrentProducts,
                  setEnabled: setSyncCurrentProducts
                }, {
                  label: 'Subscription frequency',
                  enabled: syncFrequency,
                  setEnabled: setSyncFrequency
                }, {
                  label: 'Customer tags',
                  enabled: syncCustomerTags,
                  setEnabled: setSyncCustomerTags
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-11591" data-mp="2799vbke376vj" data-mpi={index}>
                          <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-11592" data-mp="22ecpemc3tdmv" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-11593" data-mp="lwgl0pv946cc">
                            {item.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-11594" data-mp="3e37pfh4m7icn">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-11595" data-mp="306169lfzbhmw">
                      Events to Sync
                    </h3>
                    <div className="space-y-2" data-id="element-11596" data-mp="356ymhshfi1lr">
                      {[{
                  label: 'Subscription Created',
                  enabled: syncEventCreated,
                  setEnabled: setSyncEventCreated
                }, {
                  label: 'Subscription Cancelled',
                  enabled: syncEventCancelled,
                  setEnabled: setSyncEventCancelled
                }, {
                  label: 'Subscription Paused',
                  enabled: syncEventPaused,
                  setEnabled: setSyncEventPaused
                }, {
                  label: 'Subscription Resumed',
                  enabled: syncEventResumed,
                  setEnabled: setSyncEventResumed
                }, {
                  label: 'Order Created',
                  enabled: syncEventOrderCreated,
                  setEnabled: setSyncEventOrderCreated
                }, {
                  label: 'Order Shipped',
                  enabled: syncEventOrderShipped,
                  setEnabled: setSyncEventOrderShipped
                }, {
                  label: 'Payment Failed',
                  enabled: syncEventPaymentFailed,
                  setEnabled: setSyncEventPaymentFailed
                }, {
                  label: 'Payment Recovered',
                  enabled: syncEventPaymentRecovered,
                  setEnabled: setSyncEventPaymentRecovered
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-11597" data-mp="2vh07cq60l66l" data-mpi={index}>
                          <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-11598" data-mp="2qi5awlf4jj40" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-11599" data-mp="ss8an33h4l0w">
                            {item.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-11600" data-mp="4wvxdbz9wigu">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-11601" data-mp="4xp44cy8f7yz">
                      Sync Frequency
                    </h3>
                    <div className="space-y-2" data-id="element-11602" data-mp="ev38he6vs6ax">
                      {[{
                  value: 'realtime',
                  label: 'Real-time (via webhooks)'
                }, {
                  value: '15min',
                  label: 'Every 15 minutes'
                }, {
                  value: 'hourly',
                  label: 'Every hour'
                }, {
                  value: 'daily',
                  label: 'Daily'
                }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer" data-id="element-11603" data-mp="3fqayq4ac99hb">
                          <input type="radio" name="syncFrequency" value={option.value} checked={syncFrequencyType === option.value} onChange={e => {
                    setSyncFrequencyType(e.target.value);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-11604" data-mp="3assx6yynhwjc" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-11605" data-mp="2s1rpnmjgsso6">
                            {option.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </Card>}

            {/* Section 3: Webhooks */}
            <Card data-id="element-11606" data-mp="lblk45nb6o9d">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setWebhooksExpanded(!webhooksExpanded)} data-id="element-11607" data-mp="la0qci8tj9ck">
                <div data-id="element-11608" data-mp="g9w6ci8pbwhl">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11609" data-mp="2as7ae17psapl">
                    Webhooks
                  </h2>
                  <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11610" data-mp="2fj5yhmyy2d3e">
                    Send subscription events to external systems
                  </p>
                </div>
                <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-11611" data-mp="25sur1qg1xlrr">
                  {webhooksExpanded ? <X className="w-5 h-5 text-[#64748B]" data-id="element-11612" data-mp="2ti5lkq6t6jxg" /> : <Plus className="w-5 h-5 text-[#64748B]" data-id="element-11613" data-mp="2tj89cp8u4695" />}
                </button>
              </div>

              {webhooksExpanded && <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-11614" data-mp="2yffgumgvppt7">
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden mb-4" data-id="element-11615" data-mp="2jjzhc1xj51pf">
                    <table className="w-full" data-id="element-11616" data-mp="2oi116p2s551y">
                      <thead className="bg-[#F1F5F9]" data-id="element-11617" data-mp="185lzbdpgjghb">
                        <tr data-id="element-11618" data-mp="183stg0mo50ad">
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11619" data-mp="187owchp129vj">
                            Endpoint URL
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11620" data-mp="3md3op8mcq4f8">
                            Events
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11621" data-mp="455uhj2ffjf2">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11622" data-mp="38jccttl5e8gc">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E2E8F0]" data-id="element-11623" data-mp="3cm8zbvdt6opk">
                        <tr className="hover:bg-[#F8FAFC]" data-id="element-11624" data-mp="2oxhghum3zipg">
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-11625" data-mp="2ovwhnssrhdls">
                            https://api.example.com/webhooks
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#64748B]" data-id="element-11626" data-mp="39s8a9rmn7acq">
                            All events
                          </td>
                          <td className="px-4 py-3" data-id="element-11627" data-mp="3irl8tp98aeio">
                            <Badge variant="success" data-id="element-11628" data-mp="3iqtr9icguhbe">Active</Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-11629" data-mp="3nrhd7plu87yr">
                            <div className="flex items-center gap-2" data-id="element-11630" data-mp="3sq11vxrtfxut">
                              <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-11631" data-mp="3snonful9o5mi">
                                Edit
                              </button>
                              <span className="text-[#CBD5E1]" data-id="element-11632" data-mp="6azv5fjauolp">·</span>
                              <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-11633" data-mp="aaqg32m5aaib">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr className="hover:bg-[#F8FAFC]" data-id="element-11634" data-mp="uefmeeva6o3q">
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-11635" data-mp="ubtgejo1ggnm">
                            https://zapier.com/hooks/catch/123
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#64748B]" data-id="element-11636" data-mp="kg8jv11qo8l9">
                            Cancellations only
                          </td>
                          <td className="px-4 py-3" data-id="element-11637" data-mp="1d44b3q8jh8an">
                            <Badge variant="success" data-id="element-11638" data-mp="1d3m727fmxps6">Active</Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-11639" data-mp="1862urd2hzn2v">
                            <div className="flex items-center gap-2" data-id="element-11640" data-mp="13e2m8w8d3luo">
                              <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-11641" data-mp="13c85zv81chwx">
                                Edit
                              </button>
                              <span className="text-[#CBD5E1]" data-id="element-11642" data-mp="1vwzxbz8el81g">·</span>
                              <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-11643" data-mp="1r3dka5ybv254">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <Button variant="secondary" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-11645" data-mp="2xqkeqn43c2fc" />} className="border-dashed" data-id="element-11644" data-mp="2xrlzxketm5as">
                    Add Webhook
                  </Button>
                </div>}
            </Card>
          </>}
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-11646" data-mp="3lhgbzzrbfptz">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-11647" data-mp="3giwpny8t8c13">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-11648" data-mp="3ujft0s2bytwx" />
            <div className="flex-1" data-id="element-11649" data-mp="3qh2no4rhwy0o">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-11650" data-mp="3qi3zwck8zqjz">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-11651" data-mp="d0ibhqz154vg">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}